import { uniq } from 'es-toolkit';
import { createSelector } from '@reduxjs/toolkit';
import * as seedrandom from 'seedrandom';
import { Color } from 'three';

import { Member, MemberAnalysisInfo } from '@paragon/contract-library';

import { getColorFromCapacityRatio } from '../../capacityColors';
import { getSelectedItemIdentifiers } from '../../library/selection/selectors';
import { lumberColorsFromProperties } from '../../lumberColors';
import { MEMBER_COLOR } from '../../three/component/MemberVisual';
import { RootState } from '..';
import { getActiveAnalysisSet } from './getActiveAnalysisSetSelector';
import { getActiveComponentDesign } from './getActiveComponentDesignSelector';
import { getHighestCapacityForItem } from './getHighestCapacityForItemSelector';
import { getInsertingObjectInfo } from './insertionObjectSelectors';

const seed =
  '\u027b\u9609\ue5cc\u32c3\u3aea\u592c\u861b\u8495\u2aa1\u55d3\u0636\u640d\uf377\u41b6\u6ac3\uf14a' +
  '\u3fb4\uf923\u8c48\udd1a\ud3cc\ub5be\u262f\ubbe6\u6873\ue8c2\ubd97\u4699\ubba0\ue704\udee6\u17e0' +
  '\u033f\ufb75\ub8c1\u3b43\u0135\ua714\uf227\u69ee\u2c64\u976f\u34ec\u2cff\u570d\u8588\u5a7d\u0f54' +
  '\u05fd\u4d7c\u4edb\ufa79\u6437\uf67e\u1d4e\u1d35\ua46e\uab0a\ubb37\u7bae\ucbb1\u65a5\ufbef\u37eb' +
  '\uc7ed\u47c5\u8053\u3e44\u2b2c\u27a2\ue813\ud4ed\u4550\uf374\u6b9f\u1972\u52a4\ua3f7\u232d\ue90b' +
  '\ua915\u48e1\u9f10\uc3d1\u525c\ubaad\u5658\u8874\u64b7\ub1af\u8f8e\uddec\u2d10\u4655\u3042\u993e' +
  '\u1c5b\u8f96\u9c8d\ufaff\uf93d\u0241\ud484\u72e5\u089e\u3b94\u1c81\u095d\u2f77\u3156\ue1ec\u112c' +
  '\ued83\u33fd\ucc42\u5194\ua3b6\uff97\u132d\uec22\u66c2\u472b\u92a1\u1a7b\uf1ae\u9d10\u3f06\u97ed' +
  '\ud948\u1379\u2214\u6571\u228e\u6f87\u7e2e\ue73e\u4bd6\u6214\u6431\u72f0\u0abc\u72ec\ud4f2\ua1c6' +
  '\ubdf6\uebde\u26d2\uc66a\u79cc\u8f91\uc35e\u06e0\u69de\u3e60\uad8c\u84d9\u7b15\ud747\u3543\u90d1' +
  '\u8b1d\ue437\ucd03\u2eb5\u2ee0\u2711\u583e\ub4cd\udf2c\u98ad\u7862\u7d50\uefbf\u9f13\u614c\u2d7c' +
  '\ud8a2\ubaf2\u339b\u2b9d\u294d\u8af5\ubf3b\ub63f\uc98b\u83ae\u1642\u0694\uf71c\u2ad8\ueff7\u98a3' +
  '\ud742\uc099\u8b05\u3696\u3cef\ua939\u4d33\u1d9b\u5bf8\u427a\u143f\u4766\u083b\u497c\u366c\u4871' +
  '\u25ce\ube11\ube1a\ubccc\u4b10\u22a3\u3851\u7792\ue1a5\u34c2\ua008\ub48e\u5e46\ubbd5\u4ad7\u851c' +
  '\u1375\u8b43\ud726\u97c1\u4996\ua619\u03f8\ubc3b\u4d2b\u89b4\u242f\u038b\uf955\u1abf\u3800\u4490' +
  '\u7c3a\u6330\u0678\ue836\uc295\u5233\u09ae\ue929\u89cb\uad12\uc31d\u2874\u95b7\u10e5\uda7a\u08de';

const randomizeArray = <T>(array: T[]) => {
  const generator = seedrandom.xor4096(seed);
  for (let index = array.length - 1; index >= 0; index -= 1) {
    let chosenIndex = Math.floor(generator.quick() * (index + 1));
    if (chosenIndex > index) {
      chosenIndex = 0;
    }
    const chosenElement = array[chosenIndex];
    array[chosenIndex] = array[index];
    array[index] = chosenElement;
  }
};

export function* generateSingleColorIncrements(i: number): IterableIterator<number> {
  yield 0;

  const increment = 1 / 2 ** i;
  for (let value = increment; value < 1; value += increment) {
    yield value;
  }

  yield 1;
}

export function* generateColorsForRound(i: number): IterableIterator<{ r: number; g: number; b: number }> {
  for (const r of generateSingleColorIncrements(i)) {
    for (const g of generateSingleColorIncrements(i)) {
      for (const b of generateSingleColorIncrements(i)) {
        yield { r, g, b };
      }
    }
  }
}

const isGrey = (red: number, green: number, blue: number): boolean =>
  Math.abs(red - green) <= 0.12 && Math.abs(red - blue) <= 0.12;

const hasNoStrongColor = (red: number, green: number, blue: number): boolean =>
  red < 0.25 && green < 0.25 && blue < 0.25;

const chooseDistinguishableColors = (requestedColorCount: number) => {
  const generatedColors: Color[] = [];
  const duplicateColorTracker = new Set<string>();

  // Colors are generated in a series of rounds, with the RGB increments becoming smaller each time so more colors will
  //   be generated in later rounds.  This limit is in place to keep the number of rounds as low as possible.  A high
  //   number of rounds will lead to poor performance, and we should be able to generate enough colors in under five
  //   rounds.  If you don't get enough colors in a low number of rounds, you're probably filtering out too many colors
  //   and should reevaluate the filters being used.
  const MAX_ROUNDS = 5;

  for (let i = 0; i < MAX_ROUNDS; i += 1) {
    const colorsFromThisRound: Color[] = [];

    for (const { r, g, b } of generateColorsForRound(i)) {
      const color = new Color(r, g, b);
      const hexString = color.getHexString();

      if (isGrey(r, g, b) || hasNoStrongColor(r, g, b) || duplicateColorTracker.has(hexString)) {
        continue;
      }

      colorsFromThisRound.push(color);
      duplicateColorTracker.add(hexString);
    }

    if (colorsFromThisRound.length === 0) {
      throw new Error(`Round ${i} of color generation produced no new results that passed the color filter.`);
    }

    // Randomizing the array here prevents bias towards colors that are generated earlier in the round, in case not
    //   all the colors from this round are needed in order to reach `requestedColorCount`.
    randomizeArray(colorsFromThisRound);

    while (colorsFromThisRound.length > 0) {
      generatedColors.push(colorsFromThisRound.pop()!);

      if (generatedColors.length >= requestedColorCount) {
        // Randomizing the colors again before returning ensures that there won't be a noticeable pattern since members
        //   are colorized in a predictable order.
        randomizeArray(generatedColors);
        return generatedColors;
      }
    }
  }

  // If `MAX_ROUNDS` passes without enough colors being generated, something is likely broken with the algorithm.
  throw new Error(`Unable to generate ${requestedColorCount} colors in ${MAX_ROUNDS} rounds.`);
};

const getActiveComponentDesignMembers = (state: RootState) => {
  const activeComponentDesign = getActiveComponentDesign(state);
  return activeComponentDesign?.component.members ?? null;
};

const getActiveComponentDesignBraceMembers = (state: RootState) => {
  const activeComponentDesign = getActiveComponentDesign(state);
  return activeComponentDesign?.component.componentType === 'Wall'
    ? null
    : (activeComponentDesign?.component.braceMembers ?? null);
};

const getActiveComponentDesignBraceAndNonBraceMembers = createSelector(
  getActiveComponentDesignMembers,
  getActiveComponentDesignBraceMembers,
  (nonBraceMembers, braceMembers) => {
    if (!nonBraceMembers && !braceMembers) return null;
    return [...(nonBraceMembers ?? []), ...(braceMembers ?? [])];
  },
);

const getColorFromMemberLumberSelector = createSelector(getActiveComponentDesignBraceAndNonBraceMembers, (members) => {
  const colorsMap = new Map<string, Color | undefined>();
  if (members != null) {
    for (const member of members) {
      const { species, grade, treatmentType } = member.lumber;
      if (
        lumberColorsFromProperties[species] &&
        lumberColorsFromProperties[species][grade] &&
        lumberColorsFromProperties[species][grade][treatmentType]
      ) {
        colorsMap.set(member.guid, lumberColorsFromProperties[species][grade][treatmentType]);
      } else {
        colorsMap.set(member.guid, MEMBER_COLOR);
      }
    }
  }
  return (member: Member) => {
    const color = colorsMap.get(member.guid);
    if (!color) console.warn('Member color not found.');
    return color ?? MEMBER_COLOR;
  };
});

const getColorFromMemberAssemblyNameSelector = createSelector(
  getActiveComponentDesignBraceAndNonBraceMembers,
  (members) => {
    const colorsMap = new Map<string | null, Color>();
    if (members != null) {
      const memberNames = uniq(members.map((member) => member.assemblyName));
      const colors = chooseDistinguishableColors(memberNames.length);
      for (let memberIndex = 0; memberIndex < memberNames.length; memberIndex += 1) {
        colorsMap.set(memberNames[memberIndex], colors[memberIndex]);
      }
    }
    return (member: Member) => {
      const color = colorsMap.get(member.assemblyName);
      if (!color) console.warn('Member color not found.');
      return color ?? MEMBER_COLOR;
    };
  },
);

/**
 * Returns the ColorByOption that should be used for the active component.
 */
export const getActiveColorByOption = createSelector(
  (state: RootState) => state.visualOptions.colorBy,
  (state: RootState) => state.visualOptions.temporaryColorByOverride,
  (colorBy, currentComponentColorByOverride) => currentComponentColorByOverride || colorBy,
);

const getColorForMemberSelectorSelector = createSelector(
  getActiveColorByOption,
  getSelectedItemIdentifiers,
  (colorBy, selected) => {
    // Hide member colors while plates are selected.
    // This is to ensure live plating member polygons are visible
    if (selected.some((item) => item.type === 'platePair')) return () => () => MEMBER_COLOR;
    switch (colorBy) {
      case 'LumberProperties':
        return getColorFromMemberLumberSelector;
      case 'LumberGeometry':
        return getColorFromMemberAssemblyNameSelector;
      case 'Combined':
        return (state: RootState) => (member: Member) => {
          const capacity = getHighestCapacityForItem(state, member.guid, 'member');
          return capacity ? new Color(getColorFromCapacityRatio(capacity)) : MEMBER_COLOR;
        };
      default:
        return () => () => MEMBER_COLOR;
    }
  },
);

export const getColorForMemberSelector = createSelector(
  (state: RootState) => state,
  (state) => getColorForMemberSelectorSelector(state)(state),
);

export const getColorGradientForMemberSelector = createSelector(
  getInsertingObjectInfo,
  (state: RootState) => state.application.rewebbingInfo,
  getActiveColorByOption,
  getActiveAnalysisSet,
  (_, member: Member) => member,
  (insertingObjectInfo, rewebbingInfo, colorBy, analysisSet, member) => {
    if (insertingObjectInfo?.type === 'componentDesign')
      return getColorGradientForMemberFromMemberAnalysisInfo(insertingObjectInfo.memberAnalysisInfo, member.guid);

    if (rewebbingInfo)
      return getColorGradientForMemberFromMemberAnalysisInfo(rewebbingInfo.memberAnalysisInfo, member.guid);

    if (colorBy !== 'Combined') return null;

    if (!analysisSet?.capacityRatiosAlongMembers) return null;
    return getColorGradientForMember(analysisSet.capacityRatiosAlongMembers, member.guid);
  },
);

function getColorGradientForMemberFromMemberAnalysisInfo(
  memberAnalysisInfo: MemberAnalysisInfo | null | undefined,
  memberGuid: string,
) {
  return memberAnalysisInfo && memberAnalysisInfo.capacityRatio > 1
    ? getColorGradientForMember(memberAnalysisInfo.capacityRatiosAlongMembers, memberGuid)
    : null;
}

function getColorGradientForMember(
  capacityRatiosAlongMembers: { readonly [memberGuid: string]: readonly number[] },
  memberGuid: string,
) {
  return (
    capacityRatiosAlongMembers[memberGuid]?.map((capacityRatio) => getColorFromCapacityRatio(capacityRatio)) ?? null
  );
}
